import { useEffect, useRef, useState } from "react";
import { FilterSettings } from "../recoil/filterAtoms";
import { toCSSFilter } from "../utils/filterUtils";

/**
 * OpenCV 값이 하나라도 활성화돼 있는지 확인
 * false면 canvas 연산 전체 스킵
 */
function isOpenCVActive(f: FilterSettings): boolean {
  return (
    f.opencvFilter.equalizehist.strength > 0 ||
    f.opencvFilter.laplacian.strength > 0 ||
    f.opencvFilter.cvtcolor.colorSpace !== ""
  );
}

/* 히스토그램 평활화 (strength: 0~100 — 원본과 블렌딩 비율) */
function applyEqualizeHist(data: Uint8ClampedArray, strength: number) {
  const t = strength / 100;

  // 채널별 누적 히스토그램 계산
  for (const ch of [0, 1, 2] as const) {
    const hist = new Array(256).fill(0);
    for (let i = ch; i < data.length; i += 4) hist[data[i]]++;

    const cdf = new Array(256).fill(0);
    cdf[0] = hist[0];
    for (let i = 1; i < 256; i++) cdf[i] = cdf[i - 1] + hist[i];

    const cdfMin = cdf.find((v) => v > 0) ?? 0;
    const total = data.length / 4;
    const lut = cdf.map((v) => Math.round(((v - cdfMin) / (total - cdfMin)) * 255));

    for (let i = ch; i < data.length; i += 4) {
      data[i] = Math.round(data[i] * (1 - t) + lut[data[i]] * t);
    }
  }
}

/* 라플라시안 엣지 강조 (strength: 0~100 — 원본과 블렌딩 비율) */
function applyLaplacian(data: Uint8ClampedArray, width: number, height: number, strength: number) {
  const t = strength / 100;
  const src = new Uint8ClampedArray(data);
  const kernel = [0, -1, 0, -1, 4, -1, 0, -1, 0];

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;

      for (const ch of [0, 1, 2] as const) {
        let sum = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const si = ((y + ky) * width + (x + kx)) * 4 + ch;
            sum += src[si] * kernel[(ky + 1) * 3 + (kx + 1)];
          }
        }
        const edge = Math.min(255, Math.max(0, Math.abs(sum)));
        data[idx + ch] = Math.round(src[idx + ch] * (1 - t) + Math.min(255, src[idx + ch] + edge) * t);
      }
    }
  }
}

/* 색공간 변환 — 시각적 표현용 (실제 색공간 인코딩 아님) */
function applyColorSpace(data: Uint8ClampedArray, colorSpace: string) {
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i],
      g = data[i + 1],
      b = data[i + 2];

    switch (colorSpace) {
      case "GRAY": {
        const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
        data[i] = data[i + 1] = data[i + 2] = gray;
        break;
      }
      case "HSV": {
        // H채널 → R, S채널 → G, V채널 → B 로 시각화
        const max = Math.max(r, g, b),
          min = Math.min(r, g, b);
        const v = max;
        const s = max === 0 ? 0 : ((max - min) / max) * 255;
        let h = 0;
        if (max !== min) {
          const d = max - min;
          if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          else if (max === g) h = ((b - r) / d + 2) / 6;
          else h = ((r - g) / d + 4) / 6;
        }
        data[i] = Math.round(h * 255);
        data[i + 1] = Math.round(s);
        data[i + 2] = Math.round(v);
        break;
      }
      case "LAB": {
        // sRGB → Linear → XYZ → LAB (시각화용 근사)
        const rl = r / 255,
          gl = g / 255,
          bl = b / 255;
        const x = rl * 0.4124 + gl * 0.3576 + bl * 0.1805;
        const y = rl * 0.2126 + gl * 0.7152 + bl * 0.0722;
        const z = rl * 0.0193 + gl * 0.1192 + bl * 0.9505;
        const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
        const L = Math.min(255, (116 * f(y / 1.0) - 16) * 2.55);
        const A = Math.min(255, Math.max(0, 500 * (f(x / 0.9505) - f(y / 1.0)) + 128));
        const B = Math.min(255, Math.max(0, 200 * (f(y / 1.0) - f(z / 1.089)) + 128));
        data[i] = Math.round(L);
        data[i + 1] = Math.round(A);
        data[i + 2] = Math.round(B);
        break;
      }
      case "BGR": {
        data[i] = b;
        data[i + 2] = r;
        break;
      }
      case "HSV":
        break;
      case "YCrCb": {
        const Y = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
        const Cr = Math.round((r - Y) * 0.713 + 128);
        const Cb = Math.round((b - Y) * 0.564 + 128);
        data[i] = Y;
        data[i + 1] = Cr;
        data[i + 2] = Cb;
        break;
      }
      case "HLS": {
        const max = Math.max(r, g, b) / 255;
        const min = Math.min(r, g, b) / 255;
        const l = (max + min) / 2;
        const s = max === min ? 0 : l < 0.5 ? (max - min) / (max + min) : (max - min) / (2 - max - min);
        let h = 0;
        if (max !== min) {
          const nr = r / 255,
            ng = g / 255,
            nb = b / 255;
          if (max === nr) h = ((ng - nb) / (max - min) + (ng < nb ? 6 : 0)) / 6;
          else if (max === ng) h = ((nb - nr) / (max - min) + 2) / 6;
          else h = ((nr - ng) / (max - min) + 4) / 6;
        }
        data[i] = Math.round(h * 255);
        data[i + 1] = Math.round(l * 255);
        data[i + 2] = Math.round(s * 255);
        break;
      }
      case "XYZ": {
        const x = r * 0.4124 + g * 0.3576 + b * 0.1805;
        const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
        const z = r * 0.0193 + g * 0.1192 + b * 0.9505;
        data[i] = Math.min(255, Math.round(x));
        data[i + 1] = Math.min(255, Math.round(y));
        data[i + 2] = Math.min(255, Math.round(z));
        break;
      }
      case "LUV": {
        const rl = r / 255,
          gl = g / 255,
          bl = b / 255;
        const Y = rl * 0.2126 + gl * 0.7152 + bl * 0.0722;
        const X = rl * 0.4124 + gl * 0.3576 + bl * 0.1805;
        const Z = rl * 0.0193 + gl * 0.1192 + bl * 0.9505;
        const denom = X + 15 * Y + 3 * Z || 1;
        const u = (4 * X) / denom;
        const v = (9 * Y) / denom;
        const L = Y > 0.008856 ? 116 * Math.cbrt(Y) - 16 : 903.3 * Y;
        const U = 13 * L * (u - 0.1978);
        const V = 13 * L * (v - 0.4683);
        data[i] = Math.min(255, Math.max(0, Math.round(L * 2.55)));
        data[i + 1] = Math.min(255, Math.max(0, Math.round(U + 128)));
        data[i + 2] = Math.min(255, Math.max(0, Math.round(V + 128)));
        break;
      }
      case "YUV": {
        const Y = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
        const U = Math.round(-0.147 * r - 0.289 * g + 0.436 * b + 128);
        const V = Math.round(0.615 * r - 0.515 * g - 0.1 * b + 128);
        data[i] = Y;
        data[i + 1] = U;
        data[i + 2] = V;
        break;
      }
    }
  }
}

export function useFilteredImage(imageData: string | null, filterSetting: FilterSettings): string | null {
  const [processedSrc, setProcessedSrc] = useState<string | null>(imageData);
  const canvasRef = useRef<HTMLCanvasElement>(document.createElement("canvas"));
  const finalCanvasRef = useRef<HTMLCanvasElement>(document.createElement("canvas"));

  useEffect(() => {
    if (!imageData) {
      setProcessedSrc(null);
      return;
    }

    let cancelled = false;

    const cssFilterString = toCSSFilter(filterSetting);
    const hasOpenCV = isOpenCVActive(filterSetting);
    const hasCssFilter = cssFilterString !== "none";

    if (!hasOpenCV && !hasCssFilter) {
      setProcessedSrc(imageData);
      return;
    }

    const img = new Image();

    img.onload = () => {
      if (cancelled) return;

      const canvas = canvasRef.current;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      ctx.filter = "none";
      ctx.drawImage(img, 0, 0);

      if (hasOpenCV) {
        const imageDataObj = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const { data } = imageDataObj;
        const { equalizehist, laplacian, cvtcolor } = filterSetting.opencvFilter;

        if (cvtcolor.colorSpace) {
          applyColorSpace(data, cvtcolor.colorSpace);
        }

        if (equalizehist.strength > 0) {
          applyEqualizeHist(data, equalizehist.strength);
        }

        if (laplacian.strength > 0) {
          applyLaplacian(data, canvas.width, canvas.height, laplacian.strength);
        }

        ctx.putImageData(imageDataObj, 0, 0);
      }

      if (cancelled) return;

      if (hasCssFilter) {
        const finalCanvas = finalCanvasRef.current;
        finalCanvas.width = canvas.width;
        finalCanvas.height = canvas.height;

        const finalCtx = finalCanvas.getContext("2d", {
          willReadFrequently: true,
        });

        if (!finalCtx) return;

        finalCtx.filter = cssFilterString;
        finalCtx.drawImage(canvas, 0, 0);

        if (!cancelled) {
          setProcessedSrc(finalCanvas.toDataURL());
        }
      } else {
        if (!cancelled) {
          setProcessedSrc(canvas.toDataURL());
        }
      }
    };

    img.src = imageData;

    return () => {
      cancelled = true;
      img.onload = null;
    };
  }, [
    imageData,
    filterSetting.opencvFilter.equalizehist.strength,
    filterSetting.opencvFilter.laplacian.strength,
    filterSetting.opencvFilter.cvtcolor.colorSpace,
    filterSetting.brightness,
    filterSetting.contrast,
    filterSetting.saturate,
    filterSetting.blur,
  ]);

  return processedSrc;
}
